import { type ClickEvent, MenuItem } from '@szhsin/react-menu';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import Locator from '../../../../../../../shared/Locator';
import { getFetcherSettingsUrl } from '../../../../../../../shared/settings';
import * as DependenciesV2Store from '../../../../../../redux/dependenciesV2';
import { NEW_TAB_PROPS } from '../../../../../generic/Link';
import { DOCS_URL } from '../../../../../generic/Link/DocsLink';
import type { ManuallyAddedDependencyRequestKind } from '../../../../../../../typings/api';
import type { DependencyToRemove } from '../../../modals/RemoveDependency';

const QUEUE_BUILD = 'queue-build';
const VIEW_BUILD = 'view-build';
const EDIT_PACKAGE = 'edit-package';
const OVERWRITE_PACKAGE = 'overwrite-package';
const IGNORE_PACKAGE = 'ignore-package';
const ADD_AUTHENTICATION = 'add-authentication';
const COPY_LOCATOR = 'copy-locator';

const stopPropagation = (e: ClickEvent) => {
  // ReactMenu's MenuItem uses a custom ClickEvent type that doesn't directly expose the stopPropagation method
  // so we need to access the synthetic event and call stopPropagation on it.
  e.syntheticEvent.stopPropagation();
};

type MenuItemProps = {
  locator: string;
};

type RemovePackageProps = {
  revision: DependencyToRemove;
  setRevisionToRemove: (revision: DependencyToRemove | null) => void;
};

export const RemovePackage: VoidFunctionComponent<RemovePackageProps> = ({ revision, setRevisionToRemove }) => {
  return (
    <MenuItem
      className="fossa-menu__item-danger"
      key={IGNORE_PACKAGE}
      onClick={(e) => {
        setRevisionToRemove(revision);
        stopPropagation(e);
      }}
    >
      {revision.manual ? 'Delete Package' : 'Ignore package'}
    </MenuItem>
  );
};

const analyzingStatus = {
  error: '',
  resolved: false,
  unsupported: false,
  analyzing: true,
};

export const QueueBuild: VoidFunctionComponent<MenuItemProps> = ({ locator }) => {
  const dispatch = useDispatch();
  const { status } = useSelector(DependenciesV2Store.selectors.getDependency(locator)) || {};

  const queueBuild = async () => {
    // Update the dependency to an analyzing state so that it quickly updates when the user makes the build action
    dispatch(DependenciesV2Store.actions.updateDependency(locator, { status: analyzingStatus }));
    // Queue the build and add to the build watch
    try {
      await dispatch(DependenciesV2Store.actions.buildDependency(locator, true));
      await dispatch(DependenciesV2Store.actions.addAndFetchBuildWatchRevision(locator, true));
    } catch (error) {
      // If the build fails to queue revert the dependency back to its original status state
      dispatch(DependenciesV2Store.actions.updateDependency(locator, { status }));
    }
  };

  return (
    <MenuItem
      key={QUEUE_BUILD}
      onClick={(e) => {
        stopPropagation(e);
        queueBuild();
      }}
    >
      Queue build
    </MenuItem>
  );
};

type ViewBuildProps = {
  buildId: number;
  setBuildId: (buildId: number | null) => void;
};

export const ViewBuild: VoidFunctionComponent<ViewBuildProps> = ({ buildId, setBuildId }) => (
  <MenuItem
    key={VIEW_BUILD}
    onClick={(e) => {
      stopPropagation(e);
      setBuildId(buildId);
    }}
  >
    View build
  </MenuItem>
);

type EditPackageProps = {
  locator: string;
  setDependencyLocatorToEdit: (locator: string | null) => void;
};

export const EditPackage: VoidFunctionComponent<EditPackageProps> = ({ locator, setDependencyLocatorToEdit }) => (
  <MenuItem
    key={EDIT_PACKAGE}
    onClick={(e) => {
      stopPropagation(e);
      setDependencyLocatorToEdit(locator);
    }}
  >
    Edit package
  </MenuItem>
);

type OverwritePackageProps = {
  locator: string;
  setManualDependencyAddMode: (mode: ManuallyAddedDependencyRequestKind | null) => void;
  setDependencyLocatorToOverwrite: (locator: string | null) => void;
};

export const OverwritePackage: VoidFunctionComponent<OverwritePackageProps> = ({
  locator, setManualDependencyAddMode, setDependencyLocatorToOverwrite }) => (
    <MenuItem
      key={OVERWRITE_PACKAGE}
      onClick={(e) => {
        stopPropagation(e);
        setDependencyLocatorToOverwrite(locator);
        setManualDependencyAddMode('replace');
      }}
  >
      Overwrite package
    </MenuItem>
);

export const AddAuthentication: VoidFunctionComponent<MenuItemProps> = ({ locator }) => {
  const { fetcher } = Locator.parse(locator);
  const fetcherSettingsUrl = getFetcherSettingsUrl(fetcher) || `${DOCS_URL}/supported-languages`;

  return (
    <MenuItem
      key={ADD_AUTHENTICATION}
      onClick={(e) => {
        stopPropagation(e);
      }}
      href={fetcherSettingsUrl}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...NEW_TAB_PROPS}
    >
      Add authentication
    </MenuItem>
  );
};

export const CopyLocator: VoidFunctionComponent<MenuItemProps> = ({ locator }) => {
  return (
    <MenuItem
      key={COPY_LOCATOR}
      onClick={(e) => {
        stopPropagation(e);
        navigator.clipboard.writeText(locator);
      }}
    >
      Copy locator
    </MenuItem>
  );
};
